import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, CalendarDays, FileText, Layers } from 'lucide-react';
import ArticleCard from '../components/ui/ArticleCard.jsx';
import { IssueThumbnail } from '../components/ui/CoverThumbnail.jsx';
import { getIssue } from '../lib/auth';
import { normalizeIssue } from '../lib/journalFormat';

function IssueDetail() {
  const { id } = useParams();
  const [issue, setIssue] = useState(null);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    getIssue(id)
      .then((result) => {
        const nextIssue = normalizeIssue(result.data);
        setIssue(nextIssue);
        document.title = `${nextIssue.volume}, Issue ${nextIssue.issueNumber} - JAMSD`;
        setMessage('');
      })
      .catch((error) => setMessage(error.message))
      .finally(() => setLoading(false));
  }, [id]);

  return (
    <>
      <section className="bg-navy pt-24 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/issues" className="inline-flex items-center gap-2 font-sans text-sm text-slate-light hover:text-white mb-6">
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            Back to issues
          </Link>
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-8 items-end">
            <div className="flex gap-5 items-end">
              {issue && (
                <div className="hidden sm:block h-28 w-28 flex-shrink-0 overflow-hidden rounded-lg border border-navy-border shadow-lg">
                  <IssueThumbnail
                    src={issue.coverImage}
                    alt={`${issue.volume}, Issue ${issue.issueNumber} cover`}
                    volume={String(issue.volume).replace(/^Vol\.\s*/, '')}
                    issueNumber={issue.issueNumber}
                  />
                </div>
              )}
              <div>
                <p className="font-sans text-amber text-xs font-semibold uppercase tracking-widest mb-2">Issue Archive</p>
                <h1 className="font-display text-white text-3xl md:text-5xl font-bold">
                  {issue ? `${issue.volume}, Issue ${issue.issueNumber}` : 'Issue'}
                </h1>
                <p className="font-sans text-slate-light text-base mt-3 max-w-2xl">
                  {issue?.description || issue?.title || 'Browse articles assigned to this JAMSD volume and issue.'}
                </p>
              </div>
            </div>
            {issue && (
              <div className="rounded-lg border border-navy-border bg-navy-medium p-5">
                <div className="grid grid-cols-3 gap-3 text-center">
                  <div>
                    <Layers className="h-5 w-5 mx-auto text-amber mb-2" />
                    <p className="font-display text-white text-xl font-bold">{issue.raw.volume}</p>
                    <p className="font-sans text-xs text-slate-light">Volume</p>
                  </div>
                  <div>
                    <FileText className="h-5 w-5 mx-auto text-amber mb-2" />
                    <p className="font-display text-white text-xl font-bold">{issue.articleCount}</p>
                    <p className="font-sans text-xs text-slate-light">Articles</p>
                  </div>
                  <div>
                    <CalendarDays className="h-5 w-5 mx-auto text-amber mb-2" />
                    <p className="font-display text-white text-xl font-bold">{issue.year}</p>
                    <p className="font-sans text-xs text-slate-light">{issue.season}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {loading && <div className="rounded-lg border border-bg-border bg-white p-8 text-center font-sans text-slate">Loading issue...</div>}
        {message && <div className="rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{message}</div>}
        {issue && (
          <>
            <div className="flex items-center justify-between gap-4 flex-wrap mb-6">
              <div>
                <span className={`inline-flex rounded-full px-3 py-1 text-xs font-sans font-semibold ${issue.upcoming ? 'bg-amber-tint text-amber-tinttext' : 'bg-green-100 text-green-800'}`}>
                  {issue.upcoming ? 'Upcoming Issue' : 'Published Issue'}
                </span>
                <h2 className="font-display text-navy text-2xl font-bold mt-3">Articles in this Issue</h2>
              </div>
              <Link to="/articles" className="rounded border border-bg-border bg-white px-4 py-2 font-sans text-sm font-semibold text-navy hover:border-amber">
                Browse all articles
              </Link>
            </div>
            {issue.articles.length ? (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {issue.articles.map((article) => <ArticleCard key={article.id} article={article} />)}
              </div>
            ) : (
              <div className="rounded-lg border border-bg-border bg-bg-ice p-10 text-center">
                <p className="font-display text-navy text-xl font-bold">{issue.upcoming ? 'Articles forthcoming' : 'No articles assigned yet'}</p>
                <p className="font-sans text-sm text-slate mt-2">Once accepted manuscripts are assigned to this issue, they will appear here.</p>
              </div>
            )}
          </>
        )}
      </main>
    </>
  );
}

export default IssueDetail;
